import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"

// One Company Run's Research Tasks, listed by Research Category, with its
// File. Read-only: every query
// runs inside a read-only transaction.
export const loadCompanyRunResearch = createServerFn({ method: "GET" })
  .inputValidator(z.object({ companyRunId: z.string().uuid() }).strict())
  .handler(async ({ data }) => {
    const [{ assertAdminContext }, { readCompanyRunResearch }] =
      await Promise.all([
        import("@/lib/supabase/auth"),
        import("@/admin/monitoring/company-run-research"),
      ])
    // A server function is directly callable, so the route guard is not enough.
    await assertAdminContext()
    return readCompanyRunResearch(data.companyRunId)
  })

// One cell's receipt chain: its Source Check, Source Artifacts, Source Record
// Extraction and Source Records, each with its source-recorded subject.
export const loadResearchTaskReceipts = createServerFn({ method: "GET" })
  .inputValidator(
    z
      .object({
        companyRunId: z.string().uuid(),
        researchTaskId: z.string().uuid(),
        hasModelPayloads: z.boolean().default(false),
      })
      .strict()
  )
  .handler(async ({ data }) => {
    const [{ assertAdminContext }, { readResearchTaskReceipts }] =
      await Promise.all([
        import("@/lib/supabase/auth"),
        import("@/admin/monitoring/company-run-research"),
      ])
    await assertAdminContext()
    return readResearchTaskReceipts(
      data.companyRunId,
      data.researchTaskId,
      data.hasModelPayloads
    )
  })

export type CompanyRunResearch = Awaited<
  ReturnType<typeof loadCompanyRunResearch>
>
export type ResearchTaskReceipts = Awaited<
  ReturnType<typeof loadResearchTaskReceipts>
>

// One stored object's text, as the private bucket holds it: a Source
// Artifact's manifest or a page it names. The viewer holds each object's
// recorded media type and size, so it asks only for text it can show.
// Paths are content-addressed, so the pattern admits only Terminal captures.
export const loadSourceArtifactText = createServerFn({ method: "GET" })
  .inputValidator(
    z
      .object({
        privateObjectPath: z.string().regex(/^terminal\/[0-9a-f]{64}\.[a-z]+$/),
      })
      .strict()
  )
  .handler(async ({ data }) => {
    const [{ assertAdminContext }, { readSourceArtifact }] = await Promise.all([
      import("@/lib/supabase/auth"),
      import("@/terminal/research/source-capture-storage"),
    ])
    await assertAdminContext()
    return (await readSourceArtifact(data.privateObjectPath)).toString("utf8")
  })
